<template>
  <q-list data-test-id="permissions-by-category-list">
    <template
      v-for="(perms, category) in permissionsByCategory"
      :key="category"
    >
      <q-item-label
        class="bg-responsive-grey-1 text-responsive"
        header
        dense
      >
        <div class="row">
          <div class="col column justify-center">
            {{ category }}
          </div>
          <div
            v-if="showToggleAll"
            class="col-auto"
          >
            <q-btn
              color="grey-8"
              label="Toggle All"
              flat
              dense
              size="sm"
              :ripple="false"
              @click.stop="toggleGroupOfPermissions(perms)"
            />
          </div>
        </div>
      </q-item-label>
      <q-item
        v-for="(permission, index) in perms"
        :key="permission.action"
        class="q-mb-xs"
        :class="{
          'q-mb-md': isRequiredPermissionAvailableFor(permission) || index === (perms.length - 1),
          'q-pl-xl': isRequiredPermissionAvailableFor(permission)
        }"
        clickable
        @click.stop="toggleSinglePermission(permission)"
      >
        <q-item-section avatar>
          <q-checkbox
            class="no-pointer-events"
            :model-value="isPermissionSelected(permission)"
          />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-subtitle1 row items-center">
            <div class="q-mr-auto">
              {{ permission.label }}
            </div>
            <q-chip
              v-if="!permission.permitsEmployeeUsers && permission.permitsAdminUsers"
              class="text-caption"
              dense
            >
              Admin Level
              <q-tooltip>
                <div class="user-level-tooltip">
                  This permission additionally requires the user to have Admin access level.
                  <br>Employee level users will not be permitted even if assigned this permission.
                </div>
              </q-tooltip>
            </q-chip>
          </q-item-label>
          <q-item-label class="text-responsive-grey-9">
            <!-- Permissions descriptions come from HTML templates. -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="permission.description" />
          </q-item-label>
        </q-item-section>
      </q-item>
    </template>
  </q-list>
</template>

<script>
export default {
  name: 'PermissionsByCategoryList',
  props: {
    permissions: {
      type: Array,
      required: true
    },
    selectedPermissionActions: {
      type: Array,
      default: () => [],
      required: false
    },
    showToggleAll: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  emits: ['click-permission'],
  computed: {
    permissionsByCategory () {
      return _.groupBy(this.permissions, 'category')
    }
  },
  methods: {
    isPermissionSelected (permission) {
      return _.includes(this.selectedPermissionActions, permission.action)
    },
    findParentPermissionOf (permission) {
      if (!permission.dependsOnPermission) return null

      return _.find(this.permissions, { action: permission.dependsOnPermission })
    },
    findChildPermissionsOf (permission) {
      return _.filter(this.permissions, { dependsOnPermission: permission.action })
    },
    isRequiredPermissionAvailableFor (permission) {
      return !!permission.dependsOnPermission && _.some(this.permissions, { action: permission.dependsOnPermission })
    },
    toggleSinglePermission (permission) {
      // Handle permission interdependencies. Ensure parent permissions are selected when child permissions that depend on them are selected,
      // and ensure child permissions are deselected when their parent permissions are deselected.
      const isAddingPermission = !_.includes(this.selectedPermissionActions, permission.action)
      if (isAddingPermission) {
        const parentPermission = this.findParentPermissionOf(permission)
        if (parentPermission && !_.includes(this.selectedPermissionActions, parentPermission.action)) {
          this.$emit('click-permission', parentPermission)
        }
      } else { // Removing permission so remove permissions that depend on it
        _.each(this.findChildPermissionsOf(permission), childPermission => {
          if (_.includes(this.selectedPermissionActions, childPermission.action)) {
            this.$emit('click-permission', childPermission)
          }
        })
      }

      this.$emit('click-permission', permission)
    },
    toggleGroupOfPermissions (permissions) {
      const unselectedPermissions = _.filter(permissions, permission => !this.isPermissionSelected(permission))
      if (unselectedPermissions.length > 0) {
        // None or only some selected, toggle unselected on
        _.each(unselectedPermissions, permission => this.$emit('click-permission', permission))
      } else {
        // All permissions are selected, toggle all off
        _.each(permissions, permission => this.$emit('click-permission', permission))
      }
    }
  }
}
</script>

<style scoped>
.user-level-tooltip {
  font-size: 12px;
}
</style>
